/**
 * Client-side singleton. Attaches window.onerror + unhandledrejection
 * listeners, tracks user context via auth state, and forwards every
 * captured error to the shared insert path.
 *
 * Init runs once per tab from the <ObservabilityProvider> React
 * component. Direct callers use `clientLogger.error(...)` to log
 * from feature code catch blocks.
 */

'use client'

import { createBrowserClient } from '@supabase/ssr'
import type { SupabaseClient } from '@supabase/supabase-js'

import { addBreadcrumb, attachClickBreadcrumbs, attachNavigationBreadcrumbs } from './breadcrumbs'
import { getConfig, type Feature, type Severity, type UserImpact } from './config'
import { insertError } from './insert'
import { generateTraceId, getOrCreateSessionId } from './trace'

interface LogContext {
  feature?: Feature
  userImpact?: UserImpact
  severity?: Severity
  handled?: boolean
  componentName?: string
  action?: string
  metadata?: Record<string, unknown>
  traceId?: string
}

class ClientObservability {
  private supabase: SupabaseClient | null = null
  private userId: string | null = null
  private proId: string | null = null
  private userRole: string | null = null
  private initialized = false

  init(): void {
    if (this.initialized || typeof window === 'undefined') return
    this.initialized = true

    // Seed userRole from config default. Apps with dynamic role (therapist:
    // admin vs supervisor vs therapist) will overwrite via setUser once
    // their profile context resolves.
    const cfg = getConfig()
    if (cfg.defaultUserRole) this.userRole = cfg.defaultUserRole

    try {
      const url = process.env.NEXT_PUBLIC_SUPABASE_URL
      const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
      if (!url || !key) throw new Error('missing Supabase env')
      // Cast — createBrowserClient's returned type parameters conflict
      // slightly with SupabaseClient<any> even though they're structurally
      // identical. We only use .from().insert() and .auth.
      const client = createBrowserClient(url, key) as unknown as SupabaseClient
      this.supabase = client
      client.auth.onAuthStateChange((_event, session) => {
        this.userId = session?.user?.id ?? null
      })
    } catch (err) {
      console.warn('[observability] client init failed:', err)
    }

    // Window-level unhandled errors — anything that escapes React
    // boundaries + naked async throws. We don't know which feature
    // triggered them so we tag 'unknown'; the fingerprint + breadcrumbs
    // still let you trace the exact origin.
    window.addEventListener('error', (event) => {
      const err = event.error instanceof Error ? event.error : new Error(event.message || 'Unknown error')
      void this.error(err, {
        feature: 'unknown',
        userImpact: 'unknown',
        componentName: 'window',
        action: 'error',
        handled: false,
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      })
    })

    window.addEventListener('unhandledrejection', (event) => {
      const err = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
      void this.error(err, {
        feature: 'unknown',
        userImpact: 'unknown',
        componentName: 'window',
        action: 'unhandledrejection',
        handled: false,
      })
    })

    attachClickBreadcrumbs()
    attachNavigationBreadcrumbs()
    addBreadcrumb({
      kind: 'navigation',
      message: window.location.pathname + window.location.search,
    })
  }

  /**
   * Manually set user context. Useful when the auth state comes from a
   * React context (e.g. therapist app has both userId and proId).
   */
  setUser(ctx: { userId?: string; proId?: string; userRole?: string }): void {
    if (ctx.userId !== undefined) this.userId = ctx.userId
    if (ctx.proId !== undefined) this.proId = ctx.proId
    if (ctx.userRole !== undefined) this.userRole = ctx.userRole
  }

  /**
   * Log an error at 'error' severity by default. Returns the traceId
   * so callers can display a reference code to the user.
   */
  async error(error: unknown, context?: LogContext): Promise<string> {
    const traceId = context?.traceId || generateTraceId()
    if (!this.supabase) return traceId

    await insertError(this.supabase, {
      error,
      severity: context?.severity || 'error',
      feature: context?.feature,
      userImpact: context?.userImpact,
      handled: context?.handled,
      traceId,
      sessionId: getOrCreateSessionId(),
      userId: this.userId ?? undefined,
      proId: this.proId ?? undefined,
      userRole: this.userRole ?? undefined,
      componentName: context?.componentName,
      action: context?.action,
      metadata: context?.metadata,
    })
    return traceId
  }

  async warn(error: unknown, context?: Omit<LogContext, 'severity'>): Promise<string> {
    return this.error(error, { ...context, severity: 'warn' })
  }

  async critical(error: unknown, context?: Omit<LogContext, 'severity'>): Promise<string> {
    return this.error(error, { ...context, severity: 'critical' })
  }
}

// Lazily create a stub on the server so callers don't need to guard.
export const clientLogger =
  typeof window === 'undefined'
    ? ({
        init: () => {},
        setUser: () => {},
        error: async () => '',
        warn: async () => '',
        critical: async () => '',
      } as unknown as ClientObservability)
    : new ClientObservability()

// Re-export types callers commonly need
export type { Feature, Severity, UserImpact } from './config'
export { toReferenceCode } from './trace'
